import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import AllServicesButton from "./components/AllServicesButton";
import AppErrorHandler from "../AppErrorHandler";
import YourLoadsLink from "./elements/YourLoadsLink";
import AddLoadLink from "./elements/AddLoadLink";
import YourTrucksLink from "./elements/YourTrucksLink";
import AddTruckLink from "./elements/AddTruckLink";
import OrderLink from "./elements/OrderLink";
import SubmenuButton from "./components/SubmenuButton";

import { sendNavigationMetric } from "../../utils/sendMetric";
import logger from "../../logger";

import { useStore } from "../../connect";

import strings from "../../locales/strings";

import styles from "./Submenu.scss";

const cx = classnames.bind(styles);

const SubmenuInfo = React.lazy(() =>
  import(/* webpackChunkName: "SubmenuInfo" */ "./components/SubmenuInfo")
);

const Submenu = () => {
  const { header, profile, adaptive } = useStore();

  const stickyMenuRef = useRef(null);
  const cachedStickyMenuRef = useRef(null);
  const intersectionObserver = useRef(null);

  useEffect(() => {
    try {
      cachedStickyMenuRef.current = stickyMenuRef.current;
      intersectionObserver.current = new IntersectionObserver(
        ([event]) => {
          const isHidden = event.intersectionRatio < 1;

          if (
            event.boundingClientRect.y + event.boundingClientRect.height >
            window.innerHeight
          ) {
            header.handleStickyChange(false);
          } else {
            header.handleStickyChange(isHidden);
          }
        },
        { rootMargin: "0px 1000% 0px 1000%", threshold: 1 }
      );
      intersectionObserver.current.observe(cachedStickyMenuRef.current);
    } catch (error) {
      console.error("Error with IntersectionObserver mounting", error);
      logger.error(error, {
        sentryTitle: "Error with IntersectionObserver mounting",
      });
    }

    return () => {
      try {
        if (intersectionObserver.current) {
          intersectionObserver.current.unobserve(cachedStickyMenuRef.current);
        }
      } catch (error) {
        console.error("Error with IntersectionObserver unmount ", error);
        logger.error(error, {
          sentryTitle: "Error with IntersectionObserver unmount",
        });
      }
    };
  }, []);

  const needToRenderSubmenu = !window.headerComponentsController?.customSubmenu;

  const store = useStore();
  const baseUrl = store.baseUrl;

  const isPrerender = process.env.PRERENDER === "1";
  const currentBaseUrl = isPrerender ? "SSI_PATH_TO_BASE_URL" : baseUrl;

  return (
    <div
      ref={stickyMenuRef}
      id="header-submenu"
      className={`sticky-submenu hidden-by-compact-header ${cx(
        "sticky-submenu",
        {
          "is-sticky": !header.sticky,
          "is-anonim": !profile.isLoggedIn,
        }
      )}`}
    >
      <div id="header-submenu-sticky" className={cx("fixed-submenu")}>
        {needToRenderSubmenu && (
          <div className={cx("submenu")}>
            <nav className={cx("submenu-nav", "is-mobile")}>
              <SubmenuButton
                href={`https://loads.${currentBaseUrl}/?utm_source=header&utm_campaign=new_header`}
                icon="ic_search-out-s"
                dataName="search-loads-link"
                color="green"
                isFullWidth
                onClick={() => sendNavigationMetric("SUB_MENU_SEARCH_LOADS")}
              >
                {strings.searchLoads}
              </SubmenuButton>
              <SubmenuButton
                href={`https://trucks.${currentBaseUrl}/?utm_source=header&utm_campaign=new_header`}
                icon="ic_search-out-s"
                dataName="search-trucks-link"
                color="yellow"
                isFullWidth
                onClick={() => sendNavigationMetric("SUB_MENU_SEARCH_TRUCKS")}
              >
                {strings.searchTrucks}
              </SubmenuButton>
            </nav>
            <nav className={cx("submenu-nav", "is-desktop")}>
              <AllServicesButton isTablet={adaptive.isTablet} />
              <YourLoadsLink isTablet={adaptive.isTablet} color="green" />
              <AddLoadLink isTablet={adaptive.isTablet} color="green" />
              <YourTrucksLink isTablet={adaptive.isTablet} color="yellow" />
              <AddTruckLink isTablet={adaptive.isTablet} color="yellow" />
              <OrderLink isTablet={adaptive.isTablet} color="gray" />
            </nav>
            {process.env.PRERENDER !== "1" && profile.isLoggedIn && (
              <React.Suspense fallback="">
                <AppErrorHandler slot="submenu info">
                  <SubmenuInfo />
                </AppErrorHandler>
              </React.Suspense>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(Submenu);
